import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Database } from 'lucide-react';
import { Dataset } from '../../../types';
import { getTableTypeIcon, getDatasetStatusIndicator } from '../../../utils/tableUtils';
import { groupByTime } from '../../../utils/dateUtils';
import { Spinner, Button } from '../../ui';

interface SidebarDatasetSectionProps {
  datasets: Dataset[];
  selectedDatasetId?: string;
  isExpanded: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMoreDatasets: boolean;
  searchQuery: string;
  onToggleSection: () => void;
  onLoadMore: () => void;
}

interface DatasetItemProps {
  dataset: Dataset;
  isSelected: boolean;
  onClick: () => void;
}

const DatasetItem: React.FC<DatasetItemProps> = ({ dataset, isSelected, onClick }) => {
  const TypeIcon = getTableTypeIcon(dataset.table_type);
  const status = getDatasetStatusIndicator(dataset.status);
  const StatusIcon = status.icon;

  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-2.5 py-2 px-2.5 rounded-lg transition ${
        isSelected ? 'bg-white' : 'hover:bg-neutral-200/60'
      }`}
    >
      <div className="relative flex-shrink-0">
        <TypeIcon
          className={`w-4.5 h-4.5 ${isSelected ? 'text-neutral-900' : 'text-neutral-500'}`}
        />
        {(dataset.status === 'generating' || dataset.status?.startsWith('error')) && (
          <div
            className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full flex items-center justify-center ${
              isSelected ? 'bg-white' : 'bg-neutral-100'
            }`}
          >
            <StatusIcon
              className={`w-2 h-2 ${status.spinning ? 'animate-spin' : ''}`}
              style={{ color: status.color }}
            />
          </div>
        )}
      </div>
      <span
        className={`flex-1 text-13 truncate text-left ${
          isSelected ? 'text-neutral-900 font-medium' : 'text-neutral-600'
        }`}
      >
        {dataset.name}
      </span>
    </button>
  );
};

interface DatasetGroupProps {
  title: string;
  datasets: Dataset[];
  selectedDatasetId?: string;
  onDatasetClick: (id: string) => void;
}

const DatasetGroup: React.FC<DatasetGroupProps> = ({ title, datasets, selectedDatasetId, onDatasetClick }) => {
  if (datasets.length === 0) return null;

  return (
    <div className="mb-3">
      <h4 className="text-2xs font-medium uppercase tracking-widest mb-1.5 px-2.5 text-neutral-400">
        {title}
      </h4>
      <div className="flex flex-col gap-0.5">
        {datasets
          .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
          .map(dataset => (
            <DatasetItem
              key={dataset.id}
              dataset={dataset}
              isSelected={selectedDatasetId === dataset.id}
              onClick={() => onDatasetClick(dataset.id)}
            />
          ))}
      </div>
    </div>
  );
};

export const SidebarDatasetSection: React.FC<SidebarDatasetSectionProps> = ({
  datasets,
  selectedDatasetId,
  isExpanded,
  isLoading,
  isLoadingMore,
  hasMoreDatasets,
  searchQuery,
  onToggleSection,
  onLoadMore,
}) => {
  const navigate = useNavigate();

  const filteredDatasets = datasets.filter(d =>
    d.name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const groupedDatasets = groupByTime(filteredDatasets);

  const handleDatasetClick = (id: string) => {
    navigate(`/datasets/${id}`);
  };

  return (
    <div data-tour="sidebar-datasets">
      <div className="mt-2.5 mb-1.5 px-4 flex items-center justify-between">
        <button
          onClick={onToggleSection}
          className="flex items-center gap-1.5 group"
        >
          <ChevronRight
            className={`w-2.5 h-2.5 text-neutral-400 transition-transform duration-150 ${isExpanded ? 'rotate-90' : ''}`}
          />
          <h3 className="text-2xs font-semibold uppercase tracking-wider text-neutral-400 group-hover:text-neutral-600 transition">
            Recent Datasets
          </h3>
          {filteredDatasets.length > 0 && (
            <span className="text-2xs text-neutral-400">
              {filteredDatasets.length}
            </span>
          )}
        </button>
      </div>

      <div
        className="grid transition-[grid-template-rows] duration-200 ease-out"
        style={{ gridTemplateRows: isExpanded ? '1fr' : '0fr' }}
      >
        <div className="overflow-hidden min-h-0">
          <div className="flex-1 overflow-y-auto px-3 scrollbar-hide pb-1">
            {isLoading && datasets.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <Spinner size="sm" />
            </div>
          ) : filteredDatasets.length === 0 ? (
            <div className="text-center py-8 px-4">
              <Database className="w-8 h-8 mx-auto mb-3 text-neutral-200" />
              <p className="text-13 text-neutral-500">
                {searchQuery ? 'No matches found' : 'No datasets yet'}
              </p>
            </div>
          ) : (
            <>
              <DatasetGroup
                title="Today"
                datasets={groupedDatasets.today}
                selectedDatasetId={selectedDatasetId}
                onDatasetClick={handleDatasetClick}
              />
              <DatasetGroup
                title="Yesterday"
                datasets={groupedDatasets.yesterday}
                selectedDatasetId={selectedDatasetId}
                onDatasetClick={handleDatasetClick}
              />
              <DatasetGroup
                title="This Week"
                datasets={groupedDatasets.thisWeek}
                selectedDatasetId={selectedDatasetId}
                onDatasetClick={handleDatasetClick}
              />

              <DatasetGroup
                title="Older"
                datasets={groupedDatasets.older}
                selectedDatasetId={selectedDatasetId}
                onDatasetClick={handleDatasetClick}
              />

              {hasMoreDatasets && !searchQuery && (
                <Button
                  variant="ghost"
                  size="sm"
                  fullWidth
                  loading={isLoadingMore}
                  onClick={onLoadMore}
                  className="mb-2 text-primary-600 hover:text-primary-700 hover:bg-primary-50"
                >
                  Load more datasets
                </Button>
              )}
            </>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};
